import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { debounceTime, map } from "rxjs/operators";
import { HttpService } from "../../services/http.service";
import { StateService } from "../../services/state.service";
import { requestStatus } from "../admin.constants";
import { CAN_VIEW_OPTIONS, KEY_ACCOUNTS, ROLES, STATUS } from "./admin-modal.constant";
import { forkJoin } from "rxjs";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-admin-modal",
  templateUrl: "./admin-modal.component.html",
  styleUrls: ["./admin-modal.component.scss"],
})
export class AdminModalComponent implements OnInit {
  @ViewChild("otherRegionInput")
  otherRegionInput!: ElementRef<HTMLInputElement>;
  @ViewChild("otherCountryInput")
  otherCountryInput!: ElementRef<HTMLInputElement>;

  addUserFromGroup!: FormGroup;
  addEditZoneFormGroup!: FormGroup;
  addKeyAccFormGroup!: FormGroup;
  addCountryFormGroup!: FormGroup;
  addNewKeyAccount: boolean = false;
  isShareSearchInput: boolean = false;
  selectedCountry!: string;
  selectedRegion!: string;

  data: any;
  roles: any[] = ROLES;

  status = STATUS;
  keyAccounts: any[] = KEY_ACCOUNTS;
  canViewOptions: any[] = CAN_VIEW_OPTIONS;
  selectedZoneId!: string;
  rejectReason!: string;
  isApiCalling: boolean = false;
  isFormSubmitted: boolean = false;
  errMessage!: string;
  selectedEditKeyAccounts: any[] = [];
  existingKeyAccounts: any[] = [];
  filteredKeyAccounts: any[] = [];
  searchKeyAccount: FormControl = new FormControl();
  shareAutocomplete: FormControl = new FormControl();
  selectedSharedUser: any;
  emailIdsList: any[] = [];
  sharedUsers: any[] = [];
  allowExperimentEditing: boolean = true;
  showSpinner: boolean = false;
  revokeKeyAccounts: boolean = false;
  dialogConfig: any;
  selectAllKeyAccounts: boolean = false;
  validUserList: any = [];

  constructor(
    public dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private stateService: StateService,
    private httpService: HttpService,
    private translate: TranslateService
  ) {
    this.dialogConfig = config;
    this.data = config.data;

    //getting user details for shared users in share experiment
    if (this.data?.type === "share_exp") {
      if (this.data?.rowData.shared_users?.length > 0) {
        this.getUserDetails(
          this.data?.rowData.shared_users.map((user: any) => user.id)
        );
      }
    }
    if (
      this.data?.type === "approve_request" &&
      this.data?.rowData.type === "add_keyaccount"
    ) {
      this.selectedEditKeyAccounts = this.data.keyAccounts;
    }
  }

  ngOnInit(): void {
    // adding mask class dynamically
    const elm = document.getElementsByClassName("p-dialog-mask");
    elm[0].classList.add("user-management-dialog-mask");

    this.addUserFromGroup = new FormGroup({
      email: new FormControl("", [Validators.required, Validators.email]),
      fname: new FormControl(""),
      lname: new FormControl(""),
      role: new FormControl(""),
      zone: new FormControl("", [Validators.required]),
      key_account: new FormControl(""),
      beta_user: new FormControl(""),
      status: new FormControl(""),
    });

    this.addEditZoneFormGroup = new FormGroup({
      zone: new FormControl("", [Validators.required]),
      zone_admin: new FormControl("", [Validators.required]),
    });

    this.addKeyAccFormGroup = new FormGroup({
      zone_id: new FormControl("", [Validators.required]),
      country: new FormControl("", [Validators.required]),
      name: new FormControl("", [Validators.required]),
      region: new FormControl("", [Validators.required]),
      otherCountry: new FormControl(""),
      otherRegion: new FormControl(""),
    });

    if (this.data?.type === "app_edit_zone") {
      this.addEditZoneFormGroup.patchValue({
        zone: this.data?.rowData.name,
        zone_admin: this.data?.rowData.created_by,
      });
    } else if (this.data?.type === "edit_key_account") {
      this.addKeyAccFormGroup.patchValue({
        zone_id: this.data?.rowData.zone_id,
        country: this.data?.rowData.country,
        name: this.data?.rowData.name,
        region: this.data?.rowData.region,
      });
    } else if (this.data?.type === "edit_user") {
      this.addUserFromGroup.patchValue({
        email: this.data?.rowData.email,
        fname: this.data?.rowData.first_name,
        lname: this.data?.rowData.last_name,
        role: this.data?.rowData.role,
        zone: this.data?.rowData.zone,
        status: this.data?.rowData.is_active ? "active" : "inactive",
        beta_user: this.data?.rowData.is_beta_user,
      });
    }

    this.existingKeyAccounts = this.data?.rowData?.key_accounts;
    let existingKeyAccountIdMap: { [key: string]: string } = {};
    if (this.existingKeyAccounts !== undefined) {
      this.existingKeyAccounts.forEach((keyAccount: any) => {
        existingKeyAccountIdMap[keyAccount.id] = keyAccount.id;
      });
    }

    if (this.data?.keyAccounts !== undefined) {
      this.data.keyAccounts = this.data.keyAccounts.filter(
        (keyAccount: any) => existingKeyAccountIdMap[keyAccount.id] == undefined
      );
      this.filteredKeyAccounts = this.data.keyAccounts;
    }

    //key accounts search filter
    this.searchKeyAccount.valueChanges
      .pipe(debounceTime(500))
      .subscribe((inputValue: string) => {
        if (inputValue) {
          inputValue = inputValue.toLocaleLowerCase();
          this.filteredKeyAccounts = this.data?.keyAccounts.filter(
            (keyAccount: any) =>
              keyAccount.name.toLowerCase().includes(inputValue) ||
              keyAccount.region.toLowerCase().includes(inputValue) || // Filter by region
              keyAccount.country.toLowerCase().includes(inputValue) // Filter by country
          );
        } else {
          this.filteredKeyAccounts = this.data?.keyAccounts;
        }
      });
  }

  getColor(index: number) {
    let colors = ["#FFDBDB", "#E2DBFF", "#DBF5FF", "#DBE2FF"];
    return colors[index % 4];
  }

  shareExperiment() {

    const requests = this.validUserList.map((user: any) => {
      this.isFormSubmitted = true;
      this.shareAutocomplete.markAsTouched();
      this.showSpinner = true;
      const sharedUserId = user.id;
      const expId = this.data?.rowData.id;
      const url = `${this.stateService.apiList.experiments.getExperiments}/${expId}/share/${sharedUserId}`;
      return this.httpService.postMethod(url, {}).pipe(
        map((res) => ({
          user,
          response: res,
        })),
      )
    }

    );

    forkJoin(requests).subscribe({
      next: (results: any) => {
        if (results) {
          const res = results[0].response;
          this.showSpinner = false;
          results.forEach((data: any) => {
            this.data?.rowData.shared_users?.push(data.user.id);
          })
          res["rowData"] = this.data?.rowData;
          this.dialogRef.close(res);
        }
      },
      error: (error: any) => {
        this.showSpinner = false;
        this.errMessage = error.error.message;
      },
    });
  }

  onChangeShareOptions(event: any, user: any) {
    if (event.value === "Remove") {
      this.isApiCalling = true;
      const expId = this.data?.rowData.id;
      const url = `${this.stateService.apiList.experiments.getExperiments}/${expId}/share/${user.id}`;
      this.httpService.deleteMethod(url).subscribe({
        next: (res: any) => {
          if (res) {
            if (this.sharedUsers) {
              this.isApiCalling = false;
              // removing user from the shared users
              const userIndex = this.sharedUsers.indexOf(user);
              this.sharedUsers.splice(userIndex, 1);
              this.data.rowData.shared_users = this.sharedUsers.map(
                (each) => each.id
              );
            }
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  getUserDetails(sharedUsersIds: any[]) {
    this.isApiCalling = true;
    const url = this.stateService.apiList.user.sharedWith;
    const payload = { users_list: sharedUsersIds };
    this.httpService.postMethod(url, payload).subscribe({
      next: (res: any) => {
        if (res) {
          this.sharedUsers = res.map((user: any) => {
            user["permission"] = "Edit";
            return user;
          });
          this.isApiCalling = false;
        }
      },
      error: (error: any) => {
        this.isApiCalling = false;
        this.errMessage = error.error.message;
      },
    });
  }

  clearSearchText() {
    if (this.data?.type === "share_exp") {
      this.shareAutocomplete.reset();
      this.isShareSearchInput = false;
      this.validUserList = [];
    } else {
      this.searchKeyAccount.reset();
      this.filteredKeyAccounts = this.data?.keyAccounts;
    }
  }

  checkError(control: string, type: string) {
    return (
      this.addUserFromGroup.get(control)?.touched &&
      this.addUserFromGroup.get(control)!.hasError(type)
    );
  }

  closeDialog() {
    this.dialogRef.close();
  }

  submitDialog() {
    const type = this.data.type;
    switch (type) {
      case "migrate_user":
        this.migrateZone();
        break;

      case "revoke_zone":
        this.revokeZone();
        break;

      case "approve_request":
        this.approveRequest();
        break;

      case "reject_request":
        this.rejectRequest();
        break;

      case "add_user":
        this.addUser();
        break;

      case "add_edit_key_account":
        this.addEditKeyAccount();
        break;

      case "delete_exp":
        this.deleteExperiment();
        break;

      case "delete_plan":
        this.dialogRef.close(true);
        break;

      case "skip_golden_rule":
        this.dialogRef.close(true);
        break;

      case "request_zone":
        this.requestZone();
        break;

      case "revoke_zone_access":
        this.revokeUserZoneAccess();
        break;

      case "request_key_account":
        this.raiseKeyAcAccessRequest();
        break;

      case "revoke_key_account_access":
        this.revokeKeyAccountAccess();
        break;

      case "system_request_zone":
        this.addZone();
        break;

      case "appRevokeZone":
        this.deleteZone();
        break;

      case "app_edit_zone":
        this.updateZone();
        break;

      case "app_add_key_account":
        this.addNewCountry();
        break;

      case "edit_key_account":
        this.updateKeyAccount();
        break;

      case "delete_key_account":
        this.deleteKeyAccount();
        break;

      case "edit_user":
        this.editUser();
        break;

      default:
        break;
    }
  }

  revokeKeyAccountAccess() {}

  revokeUserZoneAccess() {}

  deleteKeyAccount() {
    this.isApiCalling = true;
    const url = this.stateService.apiList.admin.updateKeyAccount(
      this.data?.rowData.id
    );
    this.httpService.deleteMethod(url).subscribe({
      next: (res: any) => {
        if (res) {
          this.isApiCalling = false;
          this.dialogRef.close({
            ka_id: this.data?.rowData.id,
          });
        }
      },
      error: (error: any) => {
        this.isApiCalling = false;
        this.errMessage = error.error.message;
      },
    });
  }

  updateKeyAccount() {
    this.isFormSubmitted = true;
    if (this.addKeyAccFormGroup.valid) {
      this.isApiCalling = true;
      const url = this.stateService.apiList.admin.updateKeyAccount(
        this.data?.rowData.id
      );
      const payload = { ...this.addKeyAccFormGroup.value };
      this.httpService.putMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            this.dialogRef.close({
              ka_id: this.data?.rowData.id,
              keyAccount: payload,
            });
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  addNewCountry() {
    this.isFormSubmitted = true;
    if (this.addKeyAccFormGroup.valid) {
      this.isApiCalling = true;
      const url = this.stateService.apiList.admin.countries;
      const payload = {
        name:
          this.addKeyAccFormGroup.value.country == "Other"
            ? this.addKeyAccFormGroup.value.otherCountry
            : this.addKeyAccFormGroup.value.country,
        zone_id: this.addKeyAccFormGroup.value.zone_id,
        currency_name: "United States Dollar",
        currency_symbol: "United States Dollar",
      };
      this.httpService.postMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            //this.dialogRef.close(res);
            this.appAddNewKeyAccount();
          }
        },
        error: (error) => {
          this.isApiCalling = false;
          if (
            error.status === 400 &&
            error?.error?.message === "Country name already in use."
          ) {
            this.appAddNewKeyAccount();
          } else {
            this.errMessage = error?.error?.message;
          }
        },
      });
    }
  }

  appAddNewKeyAccount() {
    this.isFormSubmitted = true;
    if (this.addKeyAccFormGroup.valid) {
      this.isApiCalling = true;
      const url = this.stateService.apiList.admin.keyAccounts;
      const payload = { ...this.addKeyAccFormGroup.value };
      console.log("check payload:", payload);

      if (payload.region === "Other") {
        payload.region = payload.otherRegion;
      }
      if (payload.country === "Other") {
        payload.country = payload.otherCountry;
      }

      this.httpService.postMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            this.dialogRef.close(res);
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  raiseKeyAcAccessRequest() {
    this.isFormSubmitted = true;
    if (this.selectedEditKeyAccounts.length > 0) {
      const url = this.stateService.apiList.admin.requestNewKeyAccount(
        this.data?.activeZone.id
      );
      const newKeyAccountIds = this.selectedEditKeyAccounts.map(
        (keyAccount) => keyAccount.id
      );
      const payload = {
        key_account_ids: newKeyAccountIds,
      };
      this.isApiCalling = true;
      this.httpService.postMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            this.dialogRef.close(res);
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  deleteExperiment() {
    this.isApiCalling = true;
    const expId = this.data?.rowData.id;
    const url = `${this.stateService.apiList.experiments.getExperiments}/${expId}`;
    this.httpService.deleteMethod(url).subscribe({
      next: (res: any) => {
        if (res) {
          this.isApiCalling = false;
          this.dialogRef.close(res);
        }
      },
      error: (error: any) => {
        this.isApiCalling = false;
        this.errMessage = error.error.message;
      },
    });
  }

  deleteZone() {
    const zoneId = this.data?.rowData.id;
    const url = this.stateService.apiList.admin.deleteZone(zoneId);
    this.httpService.deleteMethod(url).subscribe({
      next: (res: any) => {
        if (res) {
          this.isApiCalling = false;
          this.dialogRef.close(res);
        }
      },
      error: (error: any) => {
        this.isApiCalling = false;
        this.errMessage = error.error.message;
      },
    });
  }

  addZone() {
    this.isFormSubmitted = true;
    if (this.selectedZoneId) {
      const url = this.stateService.apiList.admin.zones;
      const payload = {
        name: this.selectedZoneId,
      };
      this.isApiCalling = true;

      this.httpService.postMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            this.dialogRef.close(res);
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  updateZone() {
    this.isFormSubmitted = true;
    if (this.addEditZoneFormGroup.valid) {
      this.isApiCalling = true;
      const zoneId = this.data?.rowData.id;
      const url = this.stateService.apiList.admin.updateZone(zoneId);
      const payload = {
        name: this.addEditZoneFormGroup.value.zone,
      };
      this.httpService.putMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            this.dialogRef.close({ zone_id: zoneId, zone_name: payload.name });
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  addEditKeyAccount() {
    if (
      this.existingKeyAccounts.length !==
        this.data?.rowData?.key_accounts?.length &&
      !this.revokeKeyAccounts
    ) {
      this.revokeKeyAccounts = true;
      this.data.okBtn = this.translate.instant("ADMIN.CONFIRM");
      this.dialogConfig.header = this.translate.instant(
        "ADMIN.MODIFY_KEY_ACCOUNT_ACCESS"
      );
    } else {
      this.addKeyAccounts();
    }
  }

  addKeyAccounts() {
    const url = this.stateService.apiList.admin.syncKeyAccounts(
      this.data?.currentZone?.id,
      this.data?.rowData?.id
    );
    let newKeyAccountIds = this.selectedEditKeyAccounts.map(
      (keyAccount) => keyAccount.id
    );
    let existedKeyAccountIds = this.existingKeyAccounts.map(
      (keyAccount) => keyAccount.id
    );
    let mergedKeyAccounts = this.selectedEditKeyAccounts.concat(
      this.existingKeyAccounts
    );
    let mergedKeyAccountIds = newKeyAccountIds.concat(existedKeyAccountIds);
    const payload = {
      key_account_ids: mergedKeyAccountIds,
    };

    this.isApiCalling = true;
    this.httpService.putMethod(url, payload).subscribe({
      next: (res: any) => {
        if (res) {
          this.isApiCalling = false;
          this.data.rowData.key_accounts = mergedKeyAccounts;
          res["rowData"] = this.data.rowData;
          this.dialogRef.close(res);
        }
      },
      error: (error: any) => {
        this.isApiCalling = false;
        this.errMessage = error.error.message;
      },
    });
  }

  addUser() {
    this.addUserFromGroup.markAllAsTouched();
    this.isFormSubmitted = true;
    if (this.addUserFromGroup.valid) {
      const url = this.stateService.apiList.admin.onboardUser;
      const formData = this.addUserFromGroup.value;
      const payload = {
        email: formData.email,
        zone_id: formData.zone,
      };
      this.isApiCalling = true;

      this.httpService.postMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            this.dialogRef.close(res);
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  rejectRequest() {
    this.isFormSubmitted = true;
    if (this.rejectReason) {
      const url = `${this.stateService.apiList.admin.approveRejectRequest(
        this.data.currentZone.id,
        this.data.rowData.id
      )}`;
      const payload = {
        status: requestStatus.REJECT_REQUEST,
        remarks: this.rejectReason,
        key_account_ids: [],
      };
      this.isApiCalling = true;

      this.httpService.postMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            this.data.rowData.remarks = this.rejectReason;
            res["rowData"] = this.data.rowData;
            this.dialogRef.close(res);
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  approveRequest() {
    this.isFormSubmitted = true;
    if (
      (this.data.rowData.type === "add_keyaccount" &&
        this.selectedEditKeyAccounts.length > 0) ||
      this.data.rowData.type !== "add_keyaccount"
    ) {
      const url = `${this.stateService.apiList.admin.approveRejectRequest(
        this.data.currentZone.id,
        this.data.rowData.id
      )}`;
      let newKeyAccountIds = this.selectedEditKeyAccounts.map(
        (keyAccount) => keyAccount.id
      );
      const payload = {
        status: requestStatus.APPROVE_REQUEST,
        remarks: "",
        key_account_ids: newKeyAccountIds,
      };
      this.isApiCalling = true;

      this.httpService.postMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            res["rowData"] = this.data?.rowData;
            this.dialogRef.close(res);
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  requestZone() {
    this.isFormSubmitted = true;
    if (this.selectedZoneId) {
      const url = this.stateService.apiList.admin.requestNewZone(
        this.selectedZoneId
      );
      this.isApiCalling = true;

      this.httpService.postMethod(url, {}).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            this.dialogRef.close(res);
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  migrateZone() {
    this.isFormSubmitted = true;
    if (this.selectedZoneId) {
      const url = this.stateService.apiList.admin.migrateZone(
        this.data.currentZone.id,
        this.data.rowData.id
      );
      const payload = { zone_id: this.selectedZoneId };
      this.isApiCalling = true;

      this.httpService.postMethod(url, payload).subscribe({
        next: (res: any) => {
          if (res) {
            this.isApiCalling = false;
            this.dialogRef.close(res);
          }
        },
        error: (error: any) => {
          this.isApiCalling = false;
          this.errMessage = error.error.message;
        },
      });
    }
  }

  revokeZone() {
    const url = this.stateService.apiList.admin.revokeZone(
      this.data.currentZone.id,
      this.data.rowData.id
    );
    this.isApiCalling = true;

    this.httpService.deleteMethod(url).subscribe({
      next: (res: any) => {
        if (res) {
          this.isApiCalling = false;
          res["rowData"] = this.data.rowData;
          this.dialogRef.close(res);
        }
      },
      error: (error: any) => {
        this.isApiCalling = false;
        this.errMessage = error.error.message;
      },
    });
  }

  editUser() {
    const url = this.stateService.apiList.admin.updateUser(
      this.data?.rowData.id
    );
    const formData = this.addUserFromGroup.value;
    const payload = {
      is_active: formData.status === "active" ? true : false,
      is_beta_user: formData.beta_user,
    };
    this.isApiCalling = true;

    this.httpService.putMethod(url, payload).subscribe({
      next: (res: any) => {
        if (res) {
          this.isApiCalling = false;
          this.dialogRef.close({ user_id: this.data?.rowData.id, ...payload });
        }
      },
      error: (error: any) => {
        this.isApiCalling = false;
        this.errMessage = error.error.message;
      },
    });
  }

  onCountryChange(event: any) {
    this.selectedCountry = event.value;
    this.addKeyAccFormGroup.controls["otherCountry"].clearValidators();

    this.addKeyAccFormGroup.controls["otherCountry"].updateValueAndValidity();
    this.addKeyAccFormGroup.controls["otherCountry"].reset();
    if (this.selectedCountry === "Other") {
      this.addKeyAccFormGroup.controls["otherCountry"].setValidators([
        Validators.required,
      ]);
      this.addKeyAccFormGroup.controls["otherCountry"].updateValueAndValidity();
    }

    if (this.selectedRegion === "Other" && this.otherCountryInput) {
      setTimeout(() => {
        this.otherCountryInput.nativeElement.focus();
      }, 0);
    }
  }

  onRegionChange(event: any) {
    this.selectedRegion = event.value;
    this.addKeyAccFormGroup.controls["otherRegion"].clearValidators();

    this.addKeyAccFormGroup.controls["otherRegion"].updateValueAndValidity();
    this.addKeyAccFormGroup.controls["otherRegion"].reset();
    if (this.selectedCountry === "Other") {
      this.addKeyAccFormGroup.controls["otherRegion"].setValidators([
        Validators.required,
      ]);
      this.addKeyAccFormGroup.controls["otherRegion"].updateValueAndValidity();
    }

    // If selected region is 'Other' and the input field exists, focus it
    if (this.selectedRegion === "Other" && this.otherRegionInput) {
      setTimeout(() => {
        this.otherRegionInput.nativeElement.focus();
      }, 0);
    }
  }

  selectAllAccounts() {
    if (this.selectAllKeyAccounts) {
      // If "Select All" checkbox is checked, select all key accounts
      this.selectedEditKeyAccounts = [...this.filteredKeyAccounts];
    } else {
      // If "Select All" checkbox is unchecked, deselect all key accounts
      this.selectedEditKeyAccounts = [];
    }
  }

  searchMailIds(event: any) {

    const experiment = this.data?.rowData;
    const query = event.query;

    if (event.originalEvent?.inputType === 'insertFromPaste') {
      const users = this.parseUsers(query);

      if (users.length < 2 && !this.isValidEmail(users[0])) {
        return this.singleUserSearch(query, experiment);
      }

      this.handlePastedUsers(users, experiment);
      return;
    }

    this.singleUserSearch(query, experiment);
  }

  // Helper method to separate pasted user list
  parseUsers(query: string): string[] {
    return query
      .split(',')
      .map((e: string) => e.trim())
      .filter(Boolean);
  }

  // Helper method to validate and add authorized user
  handlePastedUsers(users: string[], experiment: any) {

    users.forEach((user: string, index: number) => {
      if (users.length - 1 === index) {
        setTimeout(() => {
          const inputEl = document.querySelector(
            '.share-exp-autocomplete input'
          ) as HTMLInputElement;

          if (inputEl) {
            inputEl.value = '';
          }
        }, 800);
      }
      if (this.validUserList.find((e: any) => e.email === user || e.name === user)) return;

      if (!this.isValidEmail(user)) {
        this.addInvalidUser(user);
        return;
      }

      this.getUserdata(user, experiment).subscribe({
        next: (res: any) => {
          this.isShareSearchInput = true;

          const mappedUsers = this.mapUserResponse(res, experiment);

          if (mappedUsers.length) {
            const userData = mappedUsers[0];

            userData.has_key_account_access =
              userData.has_key_account_access &&
              userData.email === user.trim().toLowerCase();

            if (!this.validUserList.find((e: any) => e.email === userData.email)){
              this.validUserList = [...this.validUserList, userData];
            }
            
          } else {
            const name =  [res[0]?.first_name, res[0]?.last_name].filter(Boolean).join(' ') || user;
            this.addInvalidUser(name);
          }
        },
        error: (error: any) => {
          this.isShareSearchInput = true;
          this.errMessage = error?.error?.message;
        }
      });
    });
  }


  //Helper method to modify user with no name or email
  mapUserResponse(res: any[], experiment: any) {
    
    return res
      .filter((each: any) =>
        !experiment.shared_users.includes(each.id) &&
        experiment.created_by != each.id
      )
      .map((each: any) => ({
        ...each,
        name: [each.first_name, each.last_name].filter(Boolean).join(' '),
        has_key_account_access:
          experiment.key_account_id === 'Other'
            ? true
            : experiment.shared_users.find((e: any)=> e.id === each.id) ? false : each.has_key_account_access
      }));
  }

  addInvalidUser(user: string) {
    if (this.validUserList.find((e: any) => e.email === user || e.name === user)) return;
    this.validUserList = [
      ...this.validUserList,
      { name: user, has_key_account_access: false }
    ];
  }

  //Regex to verify email
  isValidEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  //Helper method to get user details for typed input
  singleUserSearch(query: string, experiment: any) {
    this.getUserdata(query, experiment).subscribe({
      next: (res: any) => {
        this.isShareSearchInput = true;
        const sharedUserIds = new Set(
          (this.sharedUsers || []).map((user: any) => user.id)
        );
        const emailIdsList = res
          .filter((each: any) => {
            return (
              !experiment.shared_users.includes(each.id) &&
              experiment.created_by != each.id &&
              !sharedUserIds.has(each.id)
            );
          })
          .map((each: any) => {
            each.name = [each.first_name, each.last_name]
              .filter((name) => name)
              .join(" ");
            each.has_key_account_access =
              experiment.key_account_id === "Other"
                ? true
                : each.has_key_account_access;
            return each;
          });
        this.emailIdsList = emailIdsList.map((user: any) => {
          return {
            ...user,
            has_key_account_access: user.has_key_account_access
          }
        });
      },
      error: (error: any) => {
        this.isShareSearchInput = true;
        this.errMessage = error.error.message;
      },
    });
  }

  getUserdata(searchText: string, experiment: any) {
    const url = `${this.stateService.apiList.user.searchUserV3}?search=${searchText}&key_account_id=${experiment.key_account_id}`;
    return this.httpService.getMethod(url).pipe(debounceTime(500));
  }

  //Helper method to enable share button
  areAllValidUser(){
    return !this.validUserList.length || this.validUserList.some((user: any) => !user.has_key_account_access);
  }
}
